import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, Users, BookOpen, UserCog,
  Search, UserCheck, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';

const TutorAssignment = () => {
  const { api } = useAuth();
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
  ];

  const fetchData = async () => {
    try {
      const [studentsRes, tutorsRes] = await Promise.all([
        api.get('/v2/admin/users?role=student'),
        api.get('/v2/admin/users?role=tutor')
      ]);
      setStudents(studentsRes.data.users);
      setFilteredStudents(studentsRes.data.users);
      setTutors(tutorsRes.data.users);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      setFilteredStudents(students.filter(s => 
        s.first_name?.toLowerCase().includes(term) ||
        s.last_name?.toLowerCase().includes(term)
      ));
    } else {
      setFilteredStudents(students);
    }
  }, [searchTerm, students]);

  const handleAssignTutor = async (studentId, tutorId) => {
    if (!tutorId) return;
    
    setSaving(prev => ({ ...prev, [studentId]: true }));
    
    try {
      await api.post('/admin/assign-tutor', {
        student_id: studentId,
        tutor_id: tutorId
      });
      
      setStudents(prev => prev.map(s => 
        s.id === studentId ? { ...s, tutor_id: tutorId } : s
      ));
      
      const tutor = tutors.find(t => t.id === tutorId);
      toast.success(`Assigned to ${tutor?.first_name} ${tutor?.last_name}`);
    } catch (error) {
      toast.error('Failed to assign tutor');
    } finally {
      setSaving(prev => ({ ...prev, [studentId]: false }));
    }
  };

  const getTutorName = (tutorId) => {
    const tutor = tutors.find(t => t.id === tutorId);
    return tutor ? `${tutor.first_name} ${tutor.last_name}` : 'Not Assigned';
  };

  return (
    <ProLayout title="Tutor Assignment" sidebarItems={sidebarItems}>
      <div data-testid="tutor-assignment">
        {/* Info Banner */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
          <p className="text-purple-800">
            <strong>Assign tutors to students.</strong> Tutors can only view and track students assigned to them.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="pro-card text-center">
            <p className="text-2xl font-bold text-slate-800">{tutors.length}</p>
            <p className="text-sm text-slate-500">Available Tutors</p>
          </div>
          <div className="pro-card text-center">
            <p className="text-2xl font-bold text-slate-800">{students.length}</p>
            <p className="text-sm text-slate-500">Total Students</p>
          </div>
          <div className="pro-card text-center">
            <p className="text-2xl font-bold text-emerald-600">
              {students.filter(s => s.tutor_id).length}
            </p>
            <p className="text-sm text-slate-500">Assigned</p>
          </div>
          <div className="pro-card text-center">
            <p className="text-2xl font-bold text-amber-600">
              {students.filter(s => !s.tutor_id).length}
            </p>
            <p className="text-sm text-slate-500">Unassigned</p>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-students"
            />
          </div>
        </div>

        {/* Students Table */}
        <div className="pro-card">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent"></div>
            </div>
          ) : tutors.length === 0 ? (
            <div className="text-center py-12">
              <UserCog className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-semibold text-slate-700 mb-2">No Tutors Available</h3>
              <p className="text-slate-500">Create tutor accounts first in User Management</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Year / Key Stage</th>
                    <th>Current Tutor</th>
                    <th>Assign Tutor</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-8 text-slate-500">
                        No students found
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map((student) => {
                      const isSaving = saving[student.id];
                      
                      return (
                        <tr key={student.id}>
                          <td className="font-medium text-slate-800">
                            {student.first_name} {student.last_name}
                          </td>
                          <td className="text-slate-600">
                            Year {student.year_group} • {student.key_stage?.toUpperCase()}
                          </td>
                          <td>
                            {student.tutor_id ? (
                              <span className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                                <UserCheck className="w-4 h-4" />
                                {getTutorName(student.tutor_id)}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-sm">Not assigned</span>
                            )}
                          </td>
                          <td>
                            <Select
                              value={student.tutor_id || ''}
                              onValueChange={(val) => handleAssignTutor(student.id, val)}
                              disabled={isSaving}
                            >
                              <SelectTrigger 
                                className="w-[200px]" 
                                data-testid={`assign-tutor-${student.id}`}
                              >
                                <SelectValue placeholder="Select tutor..." />
                              </SelectTrigger>
                              <SelectContent>
                                {tutors.map(tutor => (
                                  <SelectItem key={tutor.id} value={tutor.id}>
                                    {tutor.first_name} {tutor.last_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Tutor Overview */}
        {tutors.length > 0 && (
          <div className="mt-8">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Tutor Overview</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tutors.map(tutor => {
                const assignedCount = students.filter(s => s.tutor_id === tutor.id).length;
                return (
                  <div key={tutor.id} className="pro-card">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="font-bold text-purple-700">
                          {tutor.first_name?.charAt(0)}{tutor.last_name?.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-800">
                          {tutor.first_name} {tutor.last_name}
                        </p>
                        <p className="text-sm text-slate-500">{tutor.email}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-purple-600">{assignedCount}</p>
                        <p className="text-xs text-slate-500">Students</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ProLayout>
  );
};

export default TutorAssignment;
